import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { Brush } from '@visx/brush';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { DataContext } from '@visx/xychart';
import { first, last } from 'lodash';

import {
    GroupConfig,
    StudioVisualizationTimeseriesConfig,
} from '@blockworks/platform/api/research/studio/visualizations';
import { useIsomorphicLayoutEffect } from '@blockworks/platform/hooks';
import { cn } from '@blockworks/ui/system';

import { BRUSH_BOX_CLASSNAME, BRUSH_BOX_HTML_ID, seriesAxisConfig } from './../timeseries.model';
import { getInitialBounds, getMultipleYDomains, useBrush } from './brush.utils';
import { BrushHandle } from './brush-handle';
import { BrushPreview } from './brush-preview';

interface BrushAreaProps<T extends {}> {
    hidden: Array<keyof T>;
    visualizationId: string;
    height: number;
    offset?: number;
    options: StudioVisualizationTimeseriesConfig<T>;
    data: T[];
    groups: GroupConfig<T>[];
    onChange: Dispatch<
        SetStateAction<
            | {
                  start: number;
                  end: number;
              }
            | undefined
        >
    >;
    interval?: string;
    params?: Record<string, string>;
    bounds?: { start: number; end: number };
}

const BrushArea = <T extends {}>({
    hidden,
    height,
    options,
    data,
    groups,
    interval,
    onChange,
    visualizationId,
}: BrushAreaProps<T>) => {
    const dataCtx = useContext(DataContext);
    const { margin = { left: 0, right: 0 }, innerWidth = 0, width = 0 } = dataCtx;
    const legendOffset = options?.legend?.position === 'bottom' ? 35 : 0;

    const { shouldThrottle, axisScaleType } = useMemo(() => {
        const isBar = groups.some(group => group.type === 'bar');
        const axisStyleType = isBar ? 'bar' : 'base';
        return { shouldThrottle: isBar, axisScaleType: seriesAxisConfig[axisStyleType] };
    }, [groups]);

    // Disable the scrollable brush for now
    const { brushRef, containerRef, setBrushPosition } = useBrush({ isScrollable: !shouldThrottle });

    const xAccessor = useCallback((row: any) => row?.[options.xAxis.column] ?? null, [options.xAxis.column]);

    // This is a workeraround for the decoupling of brush and chart data - quick filtration of the data.
    const xValues = useMemo(
        () => data.map((d, idx) => Object.assign(xAccessor(d), { externalIndex: idx })),
        [data, xAccessor],
    );

    const xBrushScale = useMemo(() => {
        return scaleBand<string>({
            range: [0, innerWidth],
            reverse: true,
            domain: xValues,
            paddingInner: 1,
            align: 0,
        });
    }, [innerWidth, axisScaleType, xValues]);

    const yBrushScale = useMemo(() => {
        const { min, max } = getMultipleYDomains(data, groups);
        return scaleLinear({
            range: [height - 12, 0],
            domain: [min, max],
            round: true,
            nice: true,
        });
    }, [height, data, groups]);

    const initialBrushPosition = useMemo(() => {
        const { start, end } = getInitialBounds(data, interval);

        return {
            start: { x: xBrushScale(xAccessor(data[start])) },
            end: { x: xBrushScale(xAccessor(data[end])) },
        };
    }, [interval, data, xAccessor, xBrushScale]);

    useIsomorphicLayoutEffect(() => {
        setBrushPosition(initialBrushPosition);
    }, [initialBrushPosition, setBrushPosition]);

    const handleChange = useCallback(
        (bounds: any) => {
            if (shouldThrottle && brushRef.current?.state.isBrushing) return;
            const overlap = xBrushScale.step();
            const accuracy = Math.ceil(1 / overlap) - 1;
            if (!bounds?.xValues?.length) return;
            let extantBounds = bounds?.xValues.filter(Boolean);

            if (accuracy > 1) {
                extantBounds = extantBounds.slice(0, extantBounds.length - (1 + accuracy));
            }

            const start = last(extantBounds) as unknown as { externalIndex: number };
            const end = first(extantBounds) as unknown as { externalIndex: number };
            const nextState = {
                end: end?.externalIndex,
                start: start?.externalIndex,
            };

            if (nextState.start !== nextState.end) {
                onChange(nextState);
            }
        },
        [shouldThrottle, xBrushScale, onChange],
    );

    if (!data) return null;

    return (
        <svg
            id={BRUSH_BOX_HTML_ID}
            ref={containerRef}
            height={height}
            width={width}
            className=" text-primary py-0.5 absolute bottom-[--bottom-margin]"
            style={{ '--bottom-margin': `${legendOffset}px` } as {}}
        >
            <Group top={3} left={margin?.left ?? 0}>
                <BrushPreview<T>
                    hidden={hidden}
                    visualizationId={visualizationId}
                    data={data}
                    groups={groups}
                    xScale={xBrushScale}
                    xAccessor={xAccessor}
                    yScale={yBrushScale}
                    xMax={innerWidth}
                    yMax={height - 12}
                />
                <Brush
                    yScale={yBrushScale}
                    xScale={xBrushScale}
                    width={innerWidth}
                    height={height - 8}
                    innerRef={brushRef}
                    onChange={shouldThrottle ? handleChange : handleChange}
                    resizeTriggerAreas={['left', 'right']}
                    brushDirection="horizontal"
                    useWindowMoveEvents
                    selectedBoxStyle={{
                        rx: '4px',
                        stroke: 'var(--color-surface-interact)',
                        fill: 'transparent',
                        className: cn(
                            // ensures screenshot helpers can grab this DOM node to compute styles
                            BRUSH_BOX_CLASSNAME,
                            'rounded-md stroke-surface-interact fill-transparent',
                        ),
                    }}
                    renderBrushHandle={BrushHandle}
                />
            </Group>
        </svg>
    );
};

export type { BrushAreaProps };

export { BrushArea };
